import { type EntitySuggestion } from '@bliks-io/contracts';
import { type SuccessorPickerRenderProps } from '@bliks-io/ui';
import { SuccessorEntityPickerModal } from '@/components/entity-pickers';
import { useEditorSuggestionSource } from './useEditorSuggestionSource';
import type { SuccessorUpdateData } from '@/pages/processes/recording/types';

interface EditorSuccessorPickerProps extends SuccessorPickerRenderProps {
  /** Scopes the recommendation rows and the "unclear successor" body's step list. */
  processId?: string;
  /** Draft node the suggestions raised here anchor to (`stableStepId`). */
  recordingId?: string;
  /** Pending suggestions on the source node, for the picker's ghost rows. */
  pendingSuggestions?: EntitySuggestion[];
}

/**
 * Fills the UI kit's `renderSuccessorPicker` slot with the recording stack's
 * `SuccessorEntityPickerModal` (full search/buckets + direct create for
 * orgFunctions/systems).
 *
 * **Semantics bridge:** the modal emits a *single-entity delta* per save
 * (`{ orgFunctions: [oneRef], successorStatus: '' }`), while the UI kit's `onSave`
 * (`handleSuccessorSubmit`) expects the **full per-field value** and diffs it
 * against the current value to decide what to materialize. So an entity pick is
 * appended onto the current `value` before handing it back; terminal
 * (unclear/none) payloads pass straight through (they intentionally reset the
 * collections). Mirrors `SuccessorCard.handleModalSave` in the recording flow.
 */
export function EditorSuccessorPicker({
  open,
  onOpenChange,
  value,
  onSave,
  type,
  excludeIds,
  allowUnclear,
  processId,
  recordingId,
  pendingSuggestions
}: EditorSuccessorPickerProps) {
  const mode = type === 'assignee' ? 'assignee' : 'successor';
  const { anchorId, allowCreate } = useEditorSuggestionSource(recordingId);

  const handleModalSave = (update: SuccessorUpdateData) => {
    const isStatusReset = update.successorStatus === 'UNCLEAR' || update.successorStatus === 'NONE';
    if (isStatusReset) {
      onSave(update);
      return;
    }
    const merged: SuccessorUpdateData = { successorStatus: '' };
    if (update.persons?.[0]) merged.persons = [...(value.persons ?? []), update.persons[0]];
    if (update.orgFunctions?.[0])
      merged.orgFunctions = [...(value.orgFunctions ?? []), update.orgFunctions[0]];
    if (update.systems?.[0]) merged.systems = [...(value.systems ?? []), update.systems[0]];
    if (update.externalContacts?.[0])
      merged.externalContacts = [...(value.externalContacts ?? []), update.externalContacts[0]];
    onSave(merged);
  };

  return (
    <SuccessorEntityPickerModal
      open={open}
      onOpenChange={onOpenChange}
      onSave={handleModalSave}
      processId={processId}
      recordingId={anchorId}
      draftProcessId={processId}
      allowCreate={allowCreate}
      mode={mode}
      // External contact is offered on every editor picker (assignee included),
      // overriding the modal's assignee-mode default that hides it. "None" keeps
      // that default (hidden for the assignee picker, shown for task/rule
      // successors); "Unclear" stays opt-in via `allowUnclear`.
      hideUnclear={mode === 'assignee' && !allowUnclear}
      hideExternalContact={false}
      excludeIds={excludeIds}
      pendingSuggestions={pendingSuggestions}
    />
  );
}
